/** Shapes of a menu board as the renderer sees it. Plain data, shared by every surface. */

export const MENU_LANGUAGES = ['en', 'ar', 'both'] as const;
export type MenuLanguage = (typeof MENU_LANGUAGES)[number];

export type MenuOrientation = 'landscape' | 'portrait';

/** A size or portion with its own price: "Small · 12 oz · 6.89". */
export interface MenuVariant {
  label: string;
  labelAr: string | null;
  /** Small print beside the label, like "12 oz" or "500 cal". */
  detail: string | null;
  price: number;
}

export interface MenuItemContent {
  id: string;
  title: string;
  titleAr: string | null;
  description: string | null;
  descriptionAr: string | null;
  ingredients: string[];
  ingredientsAr: string[];
  price: number;
  /** When set and below `price`, shown as the selling price with `price` struck through. */
  discountPrice: number | null;
  imageMediaId: string | null;
  category: string | null;
  /** Sizes with their own prices; when present they are shown instead of the single price. */
  variants: MenuVariant[];
  calories: number | null;
  /** A short label such as "NEW" or "3 TACOS". */
  badge: string | null;
  badgeAr: string | null;
  /** The board section the item sits in; null or unknown means the first section. */
  sectionId: string | null;
}

/** A group of items with a heading: "Burgers", "Autumn Specials". */
export interface MenuSection {
  id: string;
  title: string | null;
  titleAr: string | null;
  /** A second line, drawn in the design's script or note style. */
  subtitle: string | null;
  subtitleAr: string | null;
  /** A picture some designs show beside the section, ideally a cut-out on transparency. */
  imageMediaId: string | null;
}

export interface MenuBrand {
  name: string | null;
  nameAr: string | null;
  tagline: string | null;
  taglineAr: string | null;
  logoMediaId: string | null;
}

export interface MenuTheme {
  backgroundColor: string;
  surfaceColor: string;
  textColor: string;
  primaryColor: string;
  accentColor: string;
  /** A font pairing; see MENU_FONTS. */
  fontId: string;
  backgroundMediaId: string | null;
  /** One of the design's style variants; null or unknown means its first. */
  variant?: string | null;
}

export interface MenuSettings {
  title: string | null;
  titleAr: string | null;
  language: MenuLanguage;
  /** ISO 4217 code. Per board, because branches in different countries share one library. */
  currency: string;
  /** How long each page stays up when the items do not fit on one screen. */
  pageDurationMs: number;
  showDescriptions: boolean;
  showIngredients: boolean;
}

export interface MenuBoardContent {
  menuId: string;
  name: string;
  templateId: string;
  theme: MenuTheme;
  settings: MenuSettings;
  items: MenuItemContent[];
  sections: MenuSection[];
  /** Shown large by designs with a hero spot, and left out of their lists. */
  featuredItemId: string | null;
  brand: MenuBrand;
  /** Pictures a design places as decoration, in the order its description gives. */
  decorMediaIds: string[];
  /** The layout itself, when `templateId` is `custom`. */
  custom?: import('./custom.js').CustomDesign | null;
  /** The board's own grid of blocks, when `templateId` is `blocks` ("Build your own"). */
  blocks?: import('./blocks.js').BlocksLayout | null;
}

export const EMPTY_BRAND: MenuBrand = {
  name: null,
  nameAr: null,
  tagline: null,
  taglineAr: null,
  logoMediaId: null,
};
